import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Box,
  LinearProgress,
  Stack,
  IconButton,
  Tooltip,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  CapitalAmount,
  setBalanceDialog,
  setCapitalAmountTransactionsSearchBody,
} from "../../../../app/slices/capitalAmountSlice";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import dayjs from "dayjs";
import DateRangepicker from "../../../../helpers/dateRangePicker";
import { GiPayMoney, GiReceiveMoney, GiMoneyStack } from "react-icons/gi";
import { IoArrowBack } from "react-icons/io5";
import Permission from "../../../../components/userPermission/userPermission";
import CapitalAmountBalanceForm from "../form/balance";

const transferTypeOptions = [
  { id: 1, name: "دفع" },
  { id: 2, name: "سحب" },
];

const CapitalAmountTransactions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { capital_amount_id } = useParams();

  const _Transactions = useSelector(
    (state) => state.capitalAmount.capitalAmountTransactions
  );
  const _SearchBody = useSelector(
    (state) => state.capitalAmount.capitalAmountTransactions.searchBody
  );
  const _Info = useSelector(
    (state) => state.capitalAmount.capitalAmountTransactions.info
  );
  const loading = useSelector(
    (state) => state.capitalAmount.capitalAmountTransactions.loading
  );
  const _BalanceDialog = useSelector(
    (state) => state.capitalAmount.balance.dialog
  );

  const [currentPage, setCurrentPage] = useState(0);
  const [page, setPage] = useState(15);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [transferTypeId, setTransferTypeId] = useState(null);

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.3,
    },
    {
      field: "amount",
      headerName: "المبلغ",
      flex: 1,
      renderCell: (params) =>
        (params.row.amount ?? 0).toLocaleString
          ? Number(params.row.amount).toLocaleString()
          : params.row.amount,
    },
    {
      field: "transfer_type",
      headerName: "النوع",
      flex: 1,
      renderCell: (params) =>
        params.row.transfer_type?.name ??
        params.row.transfer_type_name ??
        (params.row.transfer_type_id === 1 ? "دفع" : "سحب"),
    },
    {
      field: "by_user",
      headerName: "بواسطة",
      flex: 1,
      renderCell: (params) =>
        params.row.by_user?.name ?? params.row.by_user ?? "",
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) =>
        params.row.created_at ? (
          <Stack>
            <Typography variant="body2">
              {dayjs(params.row.created_at).format("YYYY-MM-DD")}
            </Typography>
            <Typography variant="caption">
              {dayjs(params.row.created_at).format("hh:mm a")}
            </Typography>
          </Stack>
        ) : (
          ""
        ),
    },
  ];

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          المجموع الكلي : {(_Transactions.total_amount ?? 0).toLocaleString()}{" "}
          (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  useEffect(() => {
    if (!capital_amount_id) return;
    const timeout = setTimeout(() => {
      dispatch(CapitalAmount.getAllCapitalAmountTransactions(_SearchBody));
    }, 500);
    return () => clearTimeout(timeout);
  }, [_SearchBody, dispatch, capital_amount_id]);

  useEffect(() => {
    const body = {
      capital_amount_id: capital_amount_id,
      skip: currentPage * page,
      take: page,
    };
    if (startDate) body.start_date = startDate;
    if (endDate) body.end_date = endDate;
    if (transferTypeId) body.transfer_type_id = transferTypeId;
    dispatch(setCapitalAmountTransactionsSearchBody(body));
  }, [
    capital_amount_id,
    currentPage,
    dispatch,
    page,
    startDate,
    endDate,
    transferTypeId,
  ]);

  useEffect(() => {
    if (capital_amount_id)
      dispatch(CapitalAmount.getCapitalAmountInfo(capital_amount_id));
  }, [capital_amount_id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setCapitalAmountTransactionsSearchBody({}));
    };
  }, [dispatch]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            avatar={<GiMoneyStack size={20} />}
            title={
              <Stack spacing={0.5}>
                <Typography variant="h4" component="div">
                  المعاملات {_Info?.name ? `- ${_Info.name}` : ""}
                </Typography>
                <Typography variant="body2" color="primary">
                  الرصيد: IQD{" "}
                  {parseInt(_Info?.balance ?? 0).toLocaleString()}
                </Typography>
              </Stack>
            }
            action={
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Permission permission="capital_amount_view">
                  <Button
                    variant="contained"
                    color="info"
                    size="medium"
                    startIcon={<GiReceiveMoney />}
                    onClick={() =>
                      dispatch(
                        setBalanceDialog({
                          id: capital_amount_id,
                          type: "deposit",
                        })
                      )
                    }
                  >
                    اضافة رصيد
                  </Button>
                </Permission>
                <Permission permission="capital_amount_view">
                  <Button
                    variant="contained"
                    color="error"
                    size="medium"
                    startIcon={<GiPayMoney />}
                    onClick={() =>
                      dispatch(
                        setBalanceDialog({
                          id: capital_amount_id,
                          type: "withdraw",
                        })
                      )
                    }
                  >
                    سحب رصيد
                  </Button>
                </Permission>
                <Tooltip title="رجوع">
                  <IconButton onClick={() => navigate(-1)}>
                    <IoArrowBack />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          />
          {_BalanceDialog && <CapitalAmountBalanceForm />}
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={4}>
                <DateRangepicker
                  startDate={startDate}
                  endDate={endDate}
                  handleOnChange={(searchDate) => {
                    const from = searchDate[0].startDate
                      ? dayjs(searchDate[0].startDate).format("YYYY-MM-DD")
                      : null;
                    const to = searchDate[0].endDate
                      ? dayjs(searchDate[0].endDate).format("YYYY-MM-DD")
                      : null;
                    if (from !== startDate || to !== endDate) {
                      setStartDate(from);
                      setEndDate(to);
                      setCurrentPage(0);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <Autocomplete
                  options={transferTypeOptions}
                  getOptionLabel={(option) => option.name}
                  value={
                    transferTypeOptions.find((o) => o.id === transferTypeId) ??
                    null
                  }
                  isOptionEqualToValue={(option, val) => option.id === val.id}
                  onChange={(e, newValue) => {
                    setTransferTypeId(newValue ? newValue.id : null);
                    setCurrentPage(0);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="النوع" size="small" />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ height: "70vh", width: "100%" }}>
                  <DataGrid
                    rows={
                      _Transactions.data?.map((x, index) => ({
                        ...x,
                        index: index + 1,
                      })) ?? []
                    }
                    getRowId={(row) => row.id ?? row.number ?? row.index}
                    columns={columns}
                    pageSize={page}
                    page={currentPage}
                    rowCount={_Transactions.total}
                    loading={loading}
                    rowHeight={50}
                    pagination
                    paginationMode="server"
                    selectionModel={[]}
                    onPageChange={(newPage) => {
                      setCurrentPage(newPage);
                    }}
                    rowsPerPageOptions={[15, 25, 50, 100]}
                    onPageSizeChange={(newPageSize) => {
                      setPage(newPageSize);
                    }}
                    components={{
                      LoadingOverlay: LinearProgress,
                      Footer: CustomFooter,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CapitalAmountTransactions;
